import { useSnackbar, OptionsObject, SnackbarKey } from "notistack";
import { ReactNode, useCallback } from "react";
import haptics from "../native/haptics";
import AnalyticsService from "data/firestore/analytics";

interface CustomSnackbarOptions extends Omit<OptionsObject, "variant"> {
  variant?:
    | "default"
    | "error"
    | "success"
    | "warning"
    | "info"
    | "streak"
    | "kudos";
  action?: (key: SnackbarKey) => ReactNode;
}

/**
 * Custom hook that wraps useSnackbar and automatically adds click-to-dismiss functionality
 * and haptic feedback for every toast
 */
export const useCustomSnackbar = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // Stable identity (notistack's enqueue/close are stable) so consumers that
  // pass this into useCallback/memo deps don't re-render on every render.
  const customEnqueueSnackbar = useCallback(
    (message: string, options: CustomSnackbarOptions = {}): SnackbarKey => {
      if (options.variant === "streak" || options.variant === "kudos") {
        haptics.celebrate();
      } else if (options.variant === "error" || options.variant === "warning") {
        haptics.quick(true);
      } else {
        haptics.buzz();
      }

      // Create a custom action that dismisses the snackbar when clicked
      const dismissAction = (key: SnackbarKey) => (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            cursor: "pointer",
            zIndex: 1,
          }}
          onClick={() => closeSnackbar(key)}
        />
      );

      // Merge the custom action with any existing options
      const enhancedOptions: CustomSnackbarOptions = {
        ...options,
        action: (key: SnackbarKey) => {
          // If there's already an action, render it with proper structure
          if (options.action) {
            return (
              <div
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {options.action(key)}
                {/* Add a small dismiss button for action snackbars */}
                <button
                  onClick={() => closeSnackbar(key)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "rgba(255, 255, 255, 0.7)",
                    cursor: "pointer",
                    fontSize: "16px",
                    marginLeft: "8px",
                    padding: "4px",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  title="Dismiss"
                >
                  ✕
                </button>
              </div>
            );
          }
          // If no action, use the full dismiss action
          return dismissAction(key);
        },
      };

      AnalyticsService.event("show_toast", { message: message, ...options });
      // Pass through custom variants - notistack is configured to support "streak" and "kudos"
      return enqueueSnackbar(message, enhancedOptions as OptionsObject);
    },
    [enqueueSnackbar, closeSnackbar]
  );

  return {
    enqueueSnackbar: customEnqueueSnackbar,
    closeSnackbar,
  };
};

export default useCustomSnackbar;
